import { useCallback } from 'react';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { apiClient } from '../services/apiClient';
import { supabase } from '../services/supabaseService';
import { REACT_APP_FN_TABLE } from '../utils/envVar';
import { showToast, showActionToast } from '../store/toaster/action';
import { addPendingAiSummaries } from '../store/pendingAiSummaries/slice';
import {
  bulkOperationKey,
  setBulkOperationError,
} from '../store/bulk/operations/slice';
import { PendingAiSummaryEntry } from '../store/pendingAiSummaries/types';
import { apiErrorMessage } from '../utils';
import { toPunycode } from '../utils/domainUtils';
import {
  changeStatusLabelType,
  CreateThreatActionType,
  CreateThreatWithExtrasPayload,
  GetThreatActionType,
  ThreatTypes,
  UpdateThreatStatusRequest,
} from '../helpers/types';
import {
  AssetSourcesResponse,
  SingleThreatDetailsType,
} from '../context/SingleThreat/SingleThreatType';
import { getAssetSourcesApi } from '../services/singleThreatApi';

// ---------------------------------------------------------------------------
// API functions (WHEEL-341d: relocated from the deleted src/store/threats/
// slice - this file is now the single home for threat server calls).
// ---------------------------------------------------------------------------

// WHEEL-370: threats picked up by brp-classifier leave the daily_scan source,
// so a daily_scan-only filter would hide them. Applied at the request layer so
// every caller (list fetch, reset defaults, export) gets it while the visible
// filter selection, redux state, and URL sync stay as the user picked them.
export const expandSourceFilters = (sources: string[]): string[] => {
  if (sources.includes('daily_scan') && !sources.includes('brp_classifier')) {
    return [...sources, 'brp_classifier'];
  }
  return sources;
};

export const getThreatApi = async (payload: any, signal?: AbortSignal) => {
  const threatName = payload.threat_name
    ? toPunycode(payload.threat_name)
    : payload.threat_name;
  let url = `v2/assets/threats?from=${payload.from}&until=${payload.until}&threat_name=${threatName}&sort=${payload.sort}&order=${payload.order}&page=${payload.page}&size=${payload.size}&channel=a`;

  if (payload.company_ids && payload.company_ids.length > 0) {
    const companyIdsQuery = payload.company_ids
      .map((id: string) => `company_ids=${id}`)
      .join('&');
    url += `&${companyIdsQuery}`;
  }

  if (payload.status && payload.status.length > 0) {
    const statusLabelsQuery = payload.status
      .map((label: string) => `status=${label}`)
      .join('&');
    url += `&${statusLabelsQuery}`;
  }

  if (payload.service && payload.service.length > 0) {
    const serviceLabelsQuery = payload.service
      .map((label: string) => `service=${label}`)
      .join('&');
    url += `&${serviceLabelsQuery}`;
  }

  if (payload.sources && payload.sources.length > 0) {
    const sourcesLabelsQuery = expandSourceFilters(payload.sources)
      .map((label: string) => `sources=${label}`)
      .join('&');
    url += `&${sourcesLabelsQuery}`;
  }

  if (payload.brp_verdict && payload.brp_verdict.length > 0) {
    const brpVerdictQuery = payload.brp_verdict
      .map((value: string) => `brp_verdict=${value}`)
      .join('&');
    url += `&${brpVerdictQuery}`;
  }

  if (payload.brp_confidence && payload.brp_confidence.length > 0) {
    const brpConfidenceQuery = payload.brp_confidence
      .map((value: string) => `brp_confidence=${value}`)
      .join('&');
    url += `&${brpConfidenceQuery}`;
  }

  if (payload.excludeCompanysId && payload.excludeCompanysId.length > 0) {
    const excludeLabelsQuery = payload.excludeCompanysId
      .map((label: string) => `exclude_company_ids=${label}`)
      .join('&');
    url += `&${excludeLabelsQuery}`;
  }

  if (
    payload.protected_asset_names &&
    payload.protected_asset_names.length > 0
  ) {
    const assetNamesQuery = payload.protected_asset_names
      .map(
        (name: string) =>
          `protected_asset_names=${encodeURIComponent(toPunycode(name))}`,
      )
      .join('&');
    url += `&${assetNamesQuery}`;
  }

  if (
    payload.exclude_protected_asset_names &&
    payload.exclude_protected_asset_names.length > 0
  ) {
    const excludeAssetNamesQuery = payload.exclude_protected_asset_names
      .map(
        (name: string) =>
          `exclude_protected_asset_names=${encodeURIComponent(
            toPunycode(name),
          )}`,
      )
      .join('&');
    url += `&${excludeAssetNamesQuery}`;
  }

  if (payload.protectedDomainMode === 'all') {
    url += `&active_only=false`;
  }
  const config: any = {
    method: 'GET',
    url: url,
    payload,
    ...(signal && { signal }),
  };
  return await apiClient(config);
};

export const getThreatDetailApi = async (payload: any) => {
  const config = {
    method: 'GET',
    url: `v2/assets/${payload.protected_id}/threats/${payload.threat_id}`,
  };
  return await apiClient(config);
};

export const changeStatusLabelApi = async (data: UpdateThreatStatusRequest) => {
  const config = {
    method: 'PUT',
    url: `v2/assets/${data.protected_asset_id}/threats/${data.threat_id}`,
    data: {
      status: data.status,
      ...(data.user_id && { user_id: data.user_id }),
      ...(data.company_id && { company_id: data.company_id }),
      ...(data.rejection_reason_id && {
        rejection_reason_id: data.rejection_reason_id,
      }),
      ...(data.custom_rejection_reason && {
        custom_rejection_reason: data.custom_rejection_reason,
      }),
    },
  };
  return await apiClient(config);
};

export const changeBulkStatusApi = async (data: any) => {
  const config = {
    method: 'POST',
    url: `v2/assets/threats/update`,
    data,
  };
  return await apiClient(config);
};

export const getStatusApi = async (payload: string) => {
  const config = {
    method: 'GET',
    url: `v2/assets/threats/statuses`,
    payload,
  };
  const res = await apiClient(config);
  return res;
};

export const threatDetailQueryKey = (
  protectedId?: string | number,
  threatId?: string | number,
) => ['threats', 'detail', String(protectedId), String(threatId)];

// ---------------------------------------------------------------------------
// Threat list (WHEEL-341d). Replaces the `threats.threatList` slice cell -
// the Home dashboard's core table/filter/pagination surface. Pagination
// stays page-based (not useInfiniteQuery) to match the existing
// Pagination/page-size UI exactly. Home.tsx snapshots its live filters into
// `listParams` only at explicit trigger points (mount, Apply, Reset,
// search submit/clear, sort, protected-domain toggle, page/page-size
// change) - matching the former imperative-dispatch behaviour where typing
// in a filter field alone never refetched.
// ---------------------------------------------------------------------------

export type ThreatListParams = GetThreatActionType;

export const threatListQueryKeyPrefix = ['threats', 'list'] as const;

export const threatListQueryKey = (params: ThreatListParams) => [
  ...threatListQueryKeyPrefix,
  params,
];

export interface ThreatListResponse {
  items: ThreatTypes[];
  total: number;
}

export const useThreatListQuery = (params: ThreatListParams | null) =>
  useQuery<ThreatListResponse>({
    queryKey: threatListQueryKey(params ?? ({} as ThreatListParams)),
    queryFn: async () => {
      const response = await getThreatApi(params);
      return response?.data ?? { items: [], total: 0 };
    },
    enabled: params !== null,
    // The former getThreatSaga hit the API on every trigger. Overriding the
    // global 30s staleTime with 0 preserves that: switching listParams back
    // to a previously-cached key (e.g. toggling protected-domains off then
    // on) always refetches instead of silently serving stale cache.
    staleTime: 0,
  });

/** Per-threat source feeds (v2/assets/{id}/feeds). Shared by the cockpit
 * header's last-source row and the Sources card — one cache entry. */
export const useAssetSourcesQuery = (threatId?: number) =>
  useQuery<AssetSourcesResponse>({
    queryKey: ['threat', 'sources', threatId],
    queryFn: async () => {
      const response = await getAssetSourcesApi(threatId as number);
      return response?.data;
    },
    enabled: !!threatId,
  });

/** Identifiers a caller can supply to locate a threat in the list cache. */
export interface ThreatCacheMatch {
  company_id?: number | string;
  protected_asset_id?: number | string;
  threat_id?: number | string;
  threat_name?: string;
}

const sameValue = (a: unknown, b: unknown) => String(a) === String(b);

/**
 * Does a cached list row refer to the same threat as `match`?
 *
 * Replaces the former `${company_id}${protected_asset_id}${threat_id}` string
 * key (WHEEL-359). That key had two problems: every part is optional on both
 * `ThreatStatusUpdatePayload` and `UpdateThreatStatusRequest`, so a payload
 * missing `company_id` produced `undefined...` and matched nothing, leaving the
 * row on screen; and with no separator, company 1 / asset 23 collided with
 * company 12 / asset 3.
 *
 * The rule now is: every identifier the caller supplies must match, and the
 * caller must supply enough to name a SINGLE row. That needs two things:
 *
 * - which threat: `threat_id` or `threat_name`
 * - whose: `company_id` or `protected_asset_id`
 *
 * Neither half alone is sufficient, because the same threat id and the same
 * domain both appear once per company/asset pair. A `threat_id`-only payload
 * used to match every company's copy and remove rows the analyst never
 * actioned. A degraded payload that cannot name one row removes nothing - the
 * row then just waits for the next refetch, which is the safe failure.
 */
const matchesThreat = (threat: ThreatTypes, match: ThreatCacheMatch) => {
  const namesThreat = match.threat_id != null || !!match.threat_name;
  const namesOwner =
    match.company_id != null || match.protected_asset_id != null;
  if (!namesThreat || !namesOwner) return false;

  if (
    match.company_id != null &&
    !sameValue(threat.company_id, match.company_id)
  ) {
    return false;
  }
  if (
    match.protected_asset_id != null &&
    !sameValue(threat.protected_asset_id, match.protected_asset_id)
  ) {
    return false;
  }

  // With an id, the id decides. `threat_name` is deliberately NOT cross-checked
  // here: the WebSocket sends a bare host (`cpanel.bs-reiseservice.de`) while a
  // list row's `threat_name` can be the full URL including path and query, so
  // comparing them would reject a match the ids already prove. WHEEL-359.
  if (match.threat_id != null) {
    return sameValue(threat.threat_id, match.threat_id);
  }
  // No id - the name is the only row identifier we have, so it must match.
  return threat.threat_name === match.threat_name;
};

/**
 * Removes a single threat from every cached list page/filter combination
 * (mirrors the old CHANGE_STATUSLABEL_SUCCESS reducer case). Shared by
 * useChangeThreatStatusMutation (status changed from the detail page or a
 * list row) and useHomeThreatSocket (status changed via the
 * threat_status_update WebSocket - e.g. bulk operations resolving).
 */
export const removeThreatFromListCache = (
  queryClient: QueryClient,
  match: ThreatCacheMatch,
) => {
  queryClient.setQueriesData<ThreatListResponse | undefined>(
    { queryKey: threatListQueryKeyPrefix },
    (prev: ThreatListResponse | undefined) => {
      if (!prev) return prev;
      const items = prev.items.filter(
        (threat: ThreatTypes) => !matchesThreat(threat, match),
      );
      const removed = prev.items.length - items.length;
      if (!removed) return prev;
      // Drop the row from `total` too. Leaving it made the header count climb
      // back up as each bulk event resolved - the exact churn SUP-3225
      // reported. WHEEL-359.
      return {
        ...prev,
        items,
        total: Math.max((prev.total ?? 0) - removed, 0),
      };
    },
  );
};

/**
 * Marks matching threats (by threat_name) as `isLoading: true` across every
 * cached list page/filter combination (mirrors CHANGE_BULK_STATUS_SUCCESS -
 * the bulk POST was accepted, per-threat resolution arrives later over the
 * threat_status_update WebSocket).
 */
export const markThreatsLoadingInListCache = (
  queryClient: QueryClient,
  items: Array<{ threat_name: string }>,
) => {
  queryClient.setQueriesData<ThreatListResponse | undefined>(
    { queryKey: threatListQueryKeyPrefix },
    (prev: ThreatListResponse | undefined) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((threat: ThreatTypes) =>
              items.find(item => item.threat_name === threat.threat_name)
                ? { ...threat, isLoading: true }
                : threat,
            ),
          }
        : prev,
  );
};

/**
 * Clears the `isLoading` flag for a single threat (by threat_name) across
 * every cached list page/filter combination (mirrors CHANGE_BULK_STATUS_
 * FAILURE as dispatched from the threat_status_update WebSocket error
 * branch for a specific threat).
 */
export const clearThreatLoadingInListCache = (
  queryClient: QueryClient,
  threatName: string | undefined,
) => {
  queryClient.setQueriesData<ThreatListResponse | undefined>(
    { queryKey: threatListQueryKeyPrefix },
    (prev: ThreatListResponse | undefined) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((threat: ThreatTypes) =>
              threat.threat_name === threatName
                ? { ...threat, isLoading: false }
                : threat,
            ),
          }
        : prev,
  );
};

export interface BulkChangeStatusItem {
  threat_name: string;
  threatId: number;
  status: string;
  user_id?: string;
  company_id: number;
  process: string;
  priority: number;
  protected_asset_id: number;
  domain: string;
  currentStatus: string;
}

/**
 * Bulk status update (WHEEL-341d). Replaces CHANGE_BULK_STATUS_BEGIN/
 * SUCCESS/FAILURE. "Success" here only means the POST was accepted - it
 * optimistically marks the affected rows `isLoading: true` exactly like the
 * former reducer; per-threat resolution (row removed/updated, isLoading
 * cleared) arrives later over the threat_status_update WebSocket, handled
 * by useHomeThreatSocket.
 */
export const useBulkChangeStatusMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: { items: BulkChangeStatusItem[] }) => {
      const response = await changeBulkStatusApi(payload);
      return response?.data?.items ?? [];
    },
    onSuccess: (items, variables) => {
      const requested = variables.items.length;
      dispatch(
        showToast(
          `Updating ${requested} threat${
            requested === 1 ? '' : 's'
          }. Progress is in the notifications panel.`,
          'info',
        ),
      );
      markThreatsLoadingInListCache(queryClient, items);
    },
    onError: (error: any, variables) => {
      const message =
        error?.response?.data?.detail?.[0]?.msg || 'Something went wrong';
      dispatch(showToast(message, 'error'));
      // The caller registers the pending operations BEFORE this POST, and rows
      // with a pending operation are hidden and unselectable. If the request
      // itself fails, flag every one of them as errored so they come back
      // pinned at the top in red and can be retried straight away, instead of
      // silently disappearing until the suppression TTL expires. WHEEL-359.
      variables.items.forEach(item => {
        dispatch(
          setBulkOperationError({
            key: bulkOperationKey(
              item.company_id,
              item.protected_asset_id,
              item.threatId,
            ),
            errorMessage: message,
          }),
        );
      });
    },
  });
};

/**
 * Threat detail - single owner (WHEEL-339). Previously fetched into BOTH the
 * threats Redux slice and SingleThreatContext local state.
 */
export const useThreatDetailQuery = (params?: {
  protected_id: string;
  threat_id: string;
}) =>
  useQuery<SingleThreatDetailsType>({
    queryKey: threatDetailQueryKey(params?.protected_id, params?.threat_id),
    queryFn: async () => {
      const response = await getThreatDetailApi(
        params as { protected_id: string; threat_id: string },
      );
      return response?.data;
    },
    enabled: !!params?.protected_id && !!params?.threat_id,
  });

/**
 * Single threat status change - shared by the list surfaces (ThreatTable,
 * ThreatCardV2) and the detail page (via SingleThreatContext).
 *
 * On success it optimistically removes the updated threat from every cached
 * list query (WHEEL-341d - previously done by dispatching
 * CHANGE_STATUSLABEL_SUCCESS to the threats reducer). This is what keeps a
 * mounted Home list in sync when the status changes from the detail page or
 * split-pane preview.
 */
const buildStatusChangeRequest = (
  data: changeStatusLabelType,
): UpdateThreatStatusRequest => ({
  protected_asset_id: data.protected_asset_id,
  threat_id: data.threat_id,
  status: data.status,
  user_id: data.user_id,
  company_id: data.company_id,
  ...(data.rejection_reason_id && {
    rejection_reason_id: data.rejection_reason_id,
  }),
  ...(data.custom_rejection_reason && {
    custom_rejection_reason: data.custom_rejection_reason,
  }),
});

/**
 * Puts an optimistically removed row back into every cached list page on
 * Undo (WHEEL-316 - previously the threats reducer's isUndo branch), with
 * its status flipped to the restored value. No-op when the row is already
 * listed (e.g. a WebSocket refetch beat the undo).
 */
const restoreThreatInListCache = (
  queryClient: QueryClient,
  threatItem: ThreatTypes,
  restoreIndex: number | undefined,
  status: string,
) => {
  queryClient.setQueriesData<ThreatListResponse | undefined>(
    { queryKey: threatListQueryKeyPrefix },
    (prev: ThreatListResponse | undefined) => {
      if (!prev) return prev;
      if (
        prev.items.some((threat: ThreatTypes) =>
          matchesThreat(threat, threatItem),
        )
      ) {
        return prev;
      }
      const restoredThreat = {
        ...threatItem,
        identification_status: {
          ...threatItem.identification_status,
          value: status,
        },
      };
      const index = Math.min(restoreIndex ?? 0, prev.items.length);
      return {
        ...prev,
        items: [
          ...prev.items.slice(0, index),
          restoredThreat,
          ...prev.items.slice(index),
        ],
        // Mirror the decrement `removeThreatFromListCache` applied on the way
        // out, or Undo leaves the header count and pagination one threat short
        // until the next refetch. WHEEL-359.
        total: (prev.total ?? 0) + 1,
      };
    },
  );
};

/**
 * Success handling shared by the mutation and the Undo callback (which runs
 * outside any component, possibly after the dispatching hook unmounted -
 * queryClient and dispatch are process-wide singletons, so the closure stays
 * valid).
 *
 * WHEEL-316 optimistic flow: a plain change removes the row from the cached
 * lists and offers Undo in the success toast; Undo re-issues the reverse
 * change and puts the row back where it was.
 */
const applyStatusChangeSuccess = (
  queryClient: QueryClient,
  dispatch: (action: any) => void,
  responseData: any,
  variables: changeStatusLabelType,
) => {
  if (variables.isUndo && variables.threatItem) {
    dispatch(showToast(`Status reverted to "${variables.status}"`, 'success'));
    restoreThreatInListCache(
      queryClient,
      variables.threatItem,
      variables.restoreIndex,
      variables.status,
    );
  } else if (
    variables.previousStatus &&
    variables.previousStatus !== variables.status
  ) {
    removeThreatFromListCache(queryClient, variables);
    const undoData: changeStatusLabelType = {
      ...variables,
      status: variables.previousStatus,
      previousStatus: undefined,
      isUndo: true,
    };
    dispatch(
      showActionToast(
        `Status changed to "${variables.status}"`,
        'success',
        'Undo',
        () => {
          changeStatusLabelApi(buildStatusChangeRequest(undoData))
            .then(response =>
              applyStatusChangeSuccess(
                queryClient,
                dispatch,
                response?.data,
                undoData,
              ),
            )
            .catch((error: unknown) => {
              dispatch(showToast(apiErrorMessage(error), 'error'));
            });
        },
        8000,
      ),
    );
  } else {
    dispatch(
      showToast(
        variables.isUndo
          ? `Status reverted to "${variables.status}"`
          : 'Status updated successfully',
        'success',
      ),
    );
    removeThreatFromListCache(queryClient, variables);
  }

  const newStatus = responseData?.items?.[0]?.status;
  queryClient.setQueryData<SingleThreatDetailsType | undefined>(
    threatDetailQueryKey(variables.protected_asset_id, variables.threat_id),
    (prev: SingleThreatDetailsType | undefined) =>
      prev
        ? {
            ...prev,
            identification_status: {
              ...prev.identification_status,
              value: newStatus || prev.identification_status?.value,
            },
          }
        : prev,
  );
};

export const useChangeThreatStatusMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (data: changeStatusLabelType) => {
      const response = await changeStatusLabelApi(
        buildStatusChangeRequest(data),
      );
      return response?.data;
    },
    // Optimistic detail-cache update (WHEEL-348): the detail page's verdict
    // buttons disable on "already this status", which only works if the
    // cached status flips before the API answers.
    onMutate: async (data: changeStatusLabelType) => {
      const detailKey = threatDetailQueryKey(
        data.protected_asset_id,
        data.threat_id,
      );
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<
        SingleThreatDetailsType | undefined
      >(detailKey);
      queryClient.setQueryData<SingleThreatDetailsType | undefined>(
        detailKey,
        (prev: SingleThreatDetailsType | undefined) =>
          prev
            ? {
                ...prev,
                identification_status: {
                  ...prev.identification_status,
                  value: data.status,
                },
              }
            : prev,
      );
      return { detailKey, previousDetail };
    },
    onSuccess: (responseData, variables) => {
      applyStatusChangeSuccess(queryClient, dispatch, responseData, variables);
    },
    onError: (error: unknown, _variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(context.detailKey, context.previousDetail);
      }
      dispatch(showToast(apiErrorMessage(error), 'error'));
    },
  });
};

// ---------------------------------------------------------------------------
// Create-threat flow + domains list (WHEEL-341c: relocated from the deleted
// createThreatSaga/createThreatWithExtrasSaga/getDomainsListSaga in
// src/store/threats/saga.ts and src/store/threats/createThreatWithExtrasSaga.ts).
// ---------------------------------------------------------------------------

export const createThreatApi = async (data: CreateThreatActionType) => {
  const config = {
    method: 'POST',
    url: `v2/assets/threats`,
    data: {
      items: data.items,
    },
  };
  return await apiClient(config);
};

export interface ProtectedDomainListItem {
  Domain: {
    Id: number;
    Name: string;
    Status?: string;
    Created?: string;
  };
  Company: {
    Id: number;
    Name?: string;
  };
}

export interface DomainsListResponse {
  Items: ProtectedDomainListItem[];
}

export const getDomainsListApi = async (
  payload?: string | { status?: string },
) => {
  const params = new URLSearchParams();

  // Handle both string and object payloads
  if (typeof payload === 'object' && payload?.status) {
    params.append('status', payload.status);
  } else if (typeof payload === 'string' && payload) {
    params.append('status', payload);
  }

  // Default to 'active' status if no status is provided
  if (params.toString() === '') {
    params.append('status', 'active');
  }

  const config = {
    method: 'GET',
    url: `domainprotection/protectedDomains?${params.toString()}`,
    payload,
  };
  return await apiClient(config);
};

/**
 * Protected domains list (WHEEL-341c). Replaces the `threats.domainList`
 * slice cell. Every consumer only needs this data while its create-threat
 * modal is open, so callers pass `enabled` bound to their modal's `open`
 * state (mirroring `useCompaniesQuery(open)` in the FN modals) instead of
 * dispatching a fetch imperatively on open like the former
 * `getDomainsListAction`.
 */
export const domainsListQueryKey = (status?: string) => [
  'threats',
  'domainsList',
  status ?? 'active',
];

export const useDomainsListQuery = (
  params?: { status?: string },
  enabled = true,
) =>
  useQuery<DomainsListResponse>({
    queryKey: domainsListQueryKey(params?.status),
    queryFn: async () => {
      const response = await getDomainsListApi(params ?? { status: 'active' });
      return response?.data ?? { Items: [] };
    },
    enabled,
  });

/**
 * Creates one or more threats. Preserves the exact success/error toasts from
 * the former createThreatSaga, including the partial-failure ("some threats
 * failed") case where the API returns per-item errors inside a 200 response.
 */
export const useCreateThreatMutation = () => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: CreateThreatActionType) => {
      const response = await createThreatApi(payload);
      return response?.data;
    },
    onSuccess: data => {
      const items = data?.items || [];
      const errorMessages: string[] = [];

      items.forEach((item: any) => {
        if (item.error) {
          errorMessages.push(
            `Threat "${item?.threat_name}" failed: ${item?.error}`,
          );
        }
      });

      if (errorMessages.length > 0) {
        if (errorMessages.length === items.length) {
          dispatch(
            showToast(
              `All threats failed:\n${errorMessages.join('\n')}`,
              'error',
            ),
          );
        } else {
          dispatch(
            showToast(
              `Some threats failed:\n${errorMessages.join('\n')}`,
              'warning',
            ),
          );
        }
      } else {
        dispatch(showToast('Threats submitted for processing', 'success'));
      }
    },
    onError: (error: any) => {
      dispatch(
        showToast(
          error?.message ||
            error?.response?.data?.items?.[0]?.msg ||
            'Something went wrong',
          'error',
        ),
      );
    },
  });
};

function getReviewerEmail(): string {
  try {
    const authUser = window.localStorage.getItem('authUserSub');
    if (authUser) {
      return JSON.parse(authUser)?.email || 'unknown';
    }
  } catch {
    /* ignore */
  }
  return 'unknown';
}

function buildFnCandidateRow(
  threatName: string,
  fnInformation: string,
  companyId: number,
  protectedAssetId: number,
) {
  const now = new Date().toISOString();
  return {
    third_party_report_id: `manual-${Date.now()}-${threatName}`,
    data_source: 'manual',
    fn_url: { full_url: threatName },
    fn_detection_ts: now,
    review_status: 'threat_created',
    reviewed_by: getReviewerEmail(),
    reviewed_at: now,
    notes: fnInformation,
    created_threat_company_id: companyId,
    created_threat_protected_asset_id: protectedAssetId,
  };
}

/**
 * Composition hook replacing createThreatWithExtrasSaga (WHEEL-343). Wraps
 * useCreateThreatMutation with the same two side-effects the saga performed
 * around the create call, in the same order:
 *
 * 1. If an AI summary exists, queue it (addPendingAiSummaries) BEFORE firing
 *    the create POST. Bulk creation is async - POST v2/assets/threats
 *    returns no threat_id, and the id arrives later over the
 *    threat_status_update WebSocket, where AiSummaryFlusher persists it.
 *    Queuing first means a fast "created" event can never beat the queue.
 * 2. Fire the create mutation, then - WITHOUT waiting for it to settle,
 *    exactly like the former `yield put(createThreatAction(...))` which is
 *    fire-and-forget in redux-saga - insert the FN candidate row into
 *    Supabase if FN information was provided. This matches the saga's actual
 *    concurrency (the Supabase insert never depended on the create call's
 *    result), not just its code order.
 */
export const useCreateThreatWithExtras = () => {
  const dispatch = useDispatch();
  const { mutate: createThreat, isPending } = useCreateThreatMutation();

  const createThreatWithExtras = useCallback(
    (payload: CreateThreatWithExtrasPayload) => {
      const { items, fnInformation, aiSummary } = payload;

      if (aiSummary) {
        const now = new Date().toISOString();
        const entries: PendingAiSummaryEntry[] = (items || []).map(item => ({
          threatName: item.threat_name,
          companyId: item.company_id ?? null,
          protectedAssetId: item.protected_asset_id ?? null,
          summary: aiSummary,
          createdAt: now,
        }));
        dispatch(addPendingAiSummaries(entries));
      }

      createThreat({ items });

      if (fnInformation) {
        (async () => {
          try {
            const rows = items.map(item =>
              buildFnCandidateRow(
                item.threat_name,
                fnInformation,
                item.company_id,
                item.protected_asset_id,
              ),
            );

            const { error } = await supabase
              .from(REACT_APP_FN_TABLE)
              .insert(rows);

            if (error) {
              console.error('Failed to insert FN candidate:', error);
              dispatch(
                showToast(
                  'False negative information could not be saved',
                  'warning',
                ),
              );
            }
          } catch (e) {
            console.error('FN candidate insert error:', e);
            dispatch(
              showToast(
                'False negative information could not be saved',
                'warning',
              ),
            );
          }
        })();
      }
    },
    [dispatch, createThreat],
  );

  return {
    createThreatWithExtras,
    isPending,
  };
};
